// Api.
import api from './Base';

// Types.
import { showError } from '../utils/showAlert';
import {
    SelfMonitorData,
    LogTailResult,
    SupportBundleJobStatus,
    RepositorySpace,
    HypertablesResult,
} from '../types/SelfMonitor';

const EMPTY_DATA: SelfMonitorData = { archiver: { jvmid: '', interval: 0, samples: [] }, agents: [] };
const EMPTY_HYPERTABLES: HypertablesResult = { available: false, generated: 0, hypertables: [] };

const SelfMonitorApi = {
    getSelfMonitor: async (minutes?: number): Promise<SelfMonitorData> => {
        const url = minutes ? `selfmonitor?minutes=${minutes}` : `selfmonitor`;
        try {
            const response = await api.get<SelfMonitorData>(url);
            return response?.data ?? EMPTY_DATA;
        } catch (error: any) {
            console.error('Failed to retrieve self-monitor data.', error);
            showError('Failed to retrieve self-monitor data', error.message || error.toString());
            return EMPTY_DATA;
        }
    },

    listLogFiles: async (): Promise<string[]> => {
        try {
            const response = await api.get<{ files: string[] }>(`selfmonitor/log`);
            return response?.data?.files ?? [];
        } catch (error: any) {
            console.error('Failed to list log files.', error);
            showError('Failed to list log files', error.message || error.toString());
            return [];
        }
    },

    tailLog: async (file: string, offset?: number, signal?: AbortSignal): Promise<LogTailResult | null> => {
        const params = new URLSearchParams({ file });
        if (offset !== undefined) {
            params.set('offset', String(offset));
        }
        try {
            const response = await api.get<LogTailResult>(`selfmonitor/log?${params.toString()}`, { signal });
            return response?.data ?? null;
        } catch (error: any) {
            if (error?.code === 'ERR_CANCELED') {
                return null;
            }
            console.error('Failed to tail log.', error);
            showError('Failed to tail log', error.message || error.toString());
            return null;
        }
    },

    getRepositorySpace: async (): Promise<RepositorySpace | null> => {
        try {
            const response = await api.get<RepositorySpace>(`selfmonitor/repository`);
            return response?.data ?? null;
        } catch (error: any) {
            console.error('Failed to retrieve repository space usage.', error);
            showError('Failed to retrieve repository space usage', error.message || error.toString());
            return null;
        }
    },

    // list_hypertables.sql is expensive - not polled, only called on mount
    // and on an explicit refresh (forceRefresh bypasses the archiver's
    // few-minutes cache of the last result).
    getHypertables: async (forceRefresh?: boolean): Promise<HypertablesResult> => {
        const url = forceRefresh ? `selfmonitor/repository/hypertables?refresh=true` : `selfmonitor/repository/hypertables`;
        try {
            const response = await api.get<HypertablesResult>(url);
            return response?.data ?? EMPTY_HYPERTABLES;
        } catch (error: any) {
            console.error('Failed to retrieve hypertable sizes.', error);
            showError('Failed to retrieve hypertable sizes', error.message || error.toString());
            return EMPTY_HYPERTABLES;
        }
    },

    startSupportBundle: async (): Promise<SupportBundleJobStatus | null> => {
        try {
            const response = await api.post<SupportBundleJobStatus>(`selfmonitor/support`, {});
            return response?.data ?? null;
        } catch (error: any) {
            console.error('Failed to start support bundle generation.', error);
            showError('Failed to start support bundle generation', error.message || error.toString());
            return null;
        }
    },

    getSupportStatus: async (): Promise<SupportBundleJobStatus | null> => {
        try {
            const response = await api.get<SupportBundleJobStatus>(`selfmonitor/support`);
            return response?.data ?? null;
        } catch (error: any) {
            console.error('Failed to retrieve support bundle status.', error);
            showError('Failed to retrieve support bundle status', error.message || error.toString());
            return null;
        }
    },

    // Fetched as a blob (rather than a plain <a href>) because the download
    // endpoint sits behind the same Authorization header every other call
    // goes through - a bare link would bypass it and get a 403.
    downloadSupportBundle: async (id: string, filenameHint?: string): Promise<void> => {
        try {
            const response = await api.get(`selfmonitor/support/download?id=${encodeURIComponent(id)}`, {
                responseType: 'blob',
            });
            const disposition: string = response.headers?.['content-disposition'] || '';
            const match = disposition.match(/filename="?([^"]+)"?/);
            const filename = match ? match[1] : (filenameHint || 'dbmarlin-support.tar.gz');
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            console.error('Failed to download support bundle.', error);
            showError('Failed to download support bundle', error.message || error.toString());
        }
    },
};

export default SelfMonitorApi;
